// Story 10.2 — Mini stat box para el Bot Card.
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface Props {
  label: string;
  value: number | string;
  /** Indicador de incremento en tiempo real (`+N` que aparece y se desvanece). */
  delta?: number;
  className?: string;
}

/** P-243: el indicador `+N` se desvanece tras `DELTA_FADE_MS` para no quedar
 *  pegado indefinidamente — comportamiento que la Spec describe ("aparece y
 *  se desvanece") pero la implementación inicial no realizaba. */
const DELTA_FADE_MS = 2000;

export function BotMetric({ label, value, delta, className }: Props): React.ReactElement {
  const [visibleDelta, setVisibleDelta] = useState<number | undefined>(delta);

  useEffect(() => {
    if (delta == null || delta <= 0) {
      setVisibleDelta(undefined);
      return;
    }
    setVisibleDelta(delta);
    const timer = window.setTimeout(() => setVisibleDelta(undefined), DELTA_FADE_MS);
    return () => window.clearTimeout(timer);
  }, [delta]);

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col rounded-md border border-border bg-muted/40 p-3',
        className,
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-2xl font-semibold tabular-nums">{value}</span>
        {visibleDelta != null && visibleDelta > 0 && (
          <span
            // P-242: solo el delta dinámico merece `role="status"` (aria-live);
            // la badge estática NO. Acá sí porque el incremento es noticia.
            role="status"
            aria-label={`${visibleDelta} nuevos`}
            className="text-xs font-medium text-success transition-opacity duration-300"
          >
            +{visibleDelta}
          </span>
        )}
      </div>
      {/* Las etiquetas largas traen un guion suave (`\u00AD`) para cortarse bien
          en tarjetas angostas; `wrap-break-word` es la red de seguridad. */}
      <span className="text-[11px] uppercase leading-tight tracking-wide text-muted-foreground wrap-break-word">
        {label}
      </span>
    </div>
  );
}
